import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { Palette, Save, ArrowLeft } from "lucide-react";
import useTenantPath from "../lib/useTenantPath";

async function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function PersonalizacaoPage() {
  const tp = useTenantPath();
  const [form, setForm] = useState({ name: "", display_name: "", tv_display_name: "", logo_url: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/branding");
      setForm({
        name: data.name || "",
        display_name: data.display_name || "",
        tv_display_name: data.tv_display_name || "",
        logo_url: data.logo_url || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) return toast.error("PNG/JPG/WEBP apenas");
    if (f.size > 600 * 1024) return toast.error("Logo > 600KB");
    set("logo_url", await readFileAsDataURL(f));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/settings/branding", form);
      toast.success("Personalização salva");
      window.dispatchEvent(new Event("branding-updated"));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-sm text-gray-500">Carregando…</div>;

  const inputCls = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-black";

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="personalizacao-page">
      <Link
        to={tp("/configuracoes")}
        data-testid="btn-back-configuracoes"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-pink-600"
      >
        <ArrowLeft size={14} /> Voltar para Configurações
      </Link>
      <div className="flex items-center gap-3">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-pink-100 text-pink-500">
          <Palette size={18} />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Personalização</h1>
          <p className="text-sm text-gray-500">Ajuste o nome exibido no app e no Painel TV, e o logo do seu salão.</p>
        </div>
      </div>

      <form onSubmit={save} className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
        <Field label="Nome da empresa">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="brand-name" />
        </Field>
        <Field label="Nome de exibição (App)">
          <input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} className={inputCls} data-testid="brand-display" />
        </Field>
        <Field label="Nome exibido no Painel TV">
          <input value={form.tv_display_name} onChange={(e) => set("tv_display_name", e.target.value)} className={inputCls} data-testid="brand-tv" />
        </Field>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">Logo</label>
          <div className="flex items-center gap-4">
            {form.logo_url ? (
              <img src={form.logo_url} alt="preview" className="h-16 w-16 rounded-full object-cover border border-gray-200" />
            ) : (
              <div className="h-16 w-16 rounded-full bg-gray-100 border border-gray-200" />
            )}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} data-testid="brand-logo"
              className="text-sm text-gray-700 file:mr-3 file:rounded-full file:border-0 file:bg-black file:px-4 file:py-2 file:text-xs file:font-semibold file:text-white" />
            {form.logo_url && (
              <button type="button" onClick={() => set("logo_url", "")} className="text-xs text-red-500 hover:underline">Remover</button>
            )}
          </div>
          <p className="mt-2 text-[11px] text-gray-500">PNG, JPG ou WEBP até 600KB.</p>
        </div>
        <button type="submit" disabled={busy} data-testid="brand-save"
          className="inline-flex items-center gap-2 rounded-full bg-black px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
          <Save size={14} />{busy ? "Salvando…" : "Salvar"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">{label}</label>
      {children}
    </div>
  );
}
